import { CondenserType, ConfigurationType, LevelType } from 'data/types';
import { condenserHasZoneCapacity } from 'utils/condenser-zone-capacity';
import { findLevelForSpace, findNearestCondenser } from 'utils/grid';

type SpacePosition = { x: number; y: number };

function getPlacedCondensersWithCapacity(
  condensers: CondenserType[]
): CondenserType[] {
  return condensers.filter(
    (condenser) =>
      condenser.position !== undefined && condenserHasZoneCapacity(condenser)
  );
}

function pickFromCandidates(
  candidates: CondenserType[],
  position: SpacePosition | null
): CondenserType | null {
  if (candidates.length === 0) return null;
  if (position) return findNearestCondenser(position, candidates);
  return candidates[0];
}

export function selectCondenserOnLevel(
  condensers: CondenserType[],
  levelUniqueId: string | null,
  position: SpacePosition | null
): CondenserType | null {
  if (!levelUniqueId) return null;
  const candidates = getPlacedCondensersWithCapacity(condensers).filter(
    (condenser) => condenser.levelUniqueId === levelUniqueId
  );
  return pickFromCandidates(candidates, position);
}

export function selectCondenserForNewZone(
  condensers: CondenserType[],
  levelUniqueId: string | null,
  position: SpacePosition | null
): CondenserType | null {
  const sameLevelCondenser = selectCondenserOnLevel(
    condensers,
    levelUniqueId,
    position
  );
  if (sameLevelCondenser) return sameLevelCondenser;
  return pickFromCandidates(
    getPlacedCondensersWithCapacity(condensers),
    position
  );
}

export function findUniqueLevelIdForSpaces(
  structure: LevelType[],
  spaceIds: string[]
): string | null {
  if (spaceIds.length === 0) return null;

  let commonLevelId: string | null = null;
  for (const spaceId of spaceIds) {
    const level = findLevelForSpace(spaceId, structure);
    if (!level) return null;
    if (commonLevelId !== null && commonLevelId !== level.uniqueId) return null;
    commonLevelId = level.uniqueId;
  }
  return commonLevelId;
}

export function selectRehomeCondenserForZoneSpaces(
  structure: LevelType[],
  condensers: CondenserType[],
  currentCondenserUniqueId: string,
  spaceIds: string[],
  position: SpacePosition | null
): CondenserType | null {
  const levelUniqueId = findUniqueLevelIdForSpaces(structure, spaceIds);
  if (!levelUniqueId) return null;

  const currentCondenser = condensers.find(
    (condenser) => condenser.uniqueId === currentCondenserUniqueId
  );
  if (currentCondenser?.levelUniqueId === levelUniqueId) return null;

  return selectCondenserOnLevel(condensers, levelUniqueId, position);
}

export function findFirstUnzonedLevelUniqueId(
  structure: LevelType[],
  configuration: ConfigurationType
): string | null {
  const zonedSpaceIds = new Set(
    configuration.condensers
      .flatMap((condenser) => condenser.zones)
      .flatMap((zone) => zone.zoneItems.map((item) => item.structureItemId))
  );

  const levelWithUnzonedSpaces = structure.find((level) =>
    level.sections.some((section) =>
      section.spaces.some(
        (space) =>
          space.conditioned !== false && !zonedSpaceIds.has(space.uniqueId)
      )
    )
  );

  return levelWithUnzonedSpaces?.uniqueId ?? null;
}
